import React, { useEffect, useState, useRef, lazy } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { ShoppingBag } from 'lucide-react';
type Product = {
  title: string;
  editionLabel?: string;
  priceNumber: number;
  priceText: string;
  thumbnailUrl?: string;
};
const FIRST_NAMES = ['María', 'Sofía', 'Valentina', 'Camila', 'Daniela', 'Lucía', 'Andrea', 'Carolina', 'Gabriela', 'Paola', 'Natalia', 'Fernanda', 'Isabella', 'Ana', 'Laura', 'Catalina', 'Mariana', 'Juliana', 'Adriana', 'Patricia', 'Sandra', 'Verónica', 'Mónica', 'Diana', 'Karla', 'Jessica', 'Elena', 'Beatriz', 'Rosa', 'Liliana'];
const LAST_NAMES = ['García', 'Rodríguez', 'Martínez', 'Hernández', 'López', 'González', 'Pérez', 'Sánchez', 'Ramírez', 'Torres', 'Flores', 'Rivera', 'Gómez', 'Díaz', 'Reyes', 'Morales', 'Ortiz', 'Castro', 'Vargas', 'Romero'];
const US_CITIES = ['Miami, FL', 'Los Angeles, CA', 'Houston, TX', 'New York, NY', 'Chicago, IL', 'San Antonio, TX', 'Dallas, TX', 'Phoenix, AZ', 'San Diego, CA', 'Orlando, FL', 'Las Vegas, NV', 'Denver, CO', 'El Paso, TX', 'Austin, TX', 'Fort Worth, TX', 'San Jose, CA', 'Tampa, FL', 'Albuquerque, NM', 'Tucson, AZ', 'Atlanta, GA', 'Jacksonville, FL', 'Charlotte, NC', 'Brooklyn, NY', 'Sacramento, CA', 'Fresno, CA'];
// Fallback products as specified
const FALLBACK_PRODUCTS: {
  product: Product;
  weight: number;
}[] = [{
  product: {
    title: 'Pack Despertar',
    editionLabel: 'Physical Book',
    priceNumber: 27,
    priceText: '$27'
  },
  weight: 70
}, {
  product: {
    title: 'Edición Digital',
    editionLabel: 'Digital Edition',
    priceNumber: 17,
    priceText: '$17'
  },
  weight: 30
}];
const PRICE_REGEX = /\$\s?(\d{1,4}(?:[.,]\d{1,2})?)\s*(?:USD)?/i;
// The main product cover — used when no product image is found in the container.
const DEFAULT_PRODUCT_THUMB = 'https://cdn.magicpatterns.com/uploads/haJqCHN4fzJaX5BJ3qxZgH/Guia_de_Costos_y_Precios_No_pierdas_ni_un_centavo_al_vender.webp';
// Patterns that identify NON-product images (payment card logos, country flags,
// tracking pixels, data URIs) so we never use them as a purchase thumbnail.
const NON_PRODUCT_IMG_PATTERNS = ['jsdelivr.net', 'flagcdn.com', 'facebook.com/tr', 'data:image', '/payment', 'card'];
function isNonProductImage(src: string | undefined): boolean {
  if (!src) return true;
  const lower = src.toLowerCase();
  return NON_PRODUCT_IMG_PATTERNS.some(p => lower.includes(p));
}
function parsePriceNumber(text: string): number | null {
  const match = text.match(PRICE_REGEX);
  if (!match) return null;
  const n = parseFloat(match[1].replace(',', '.'));
  return isNaN(n) ? null : n;
}
function formatPrice(n: number): string {
  return `$${n.toFixed(2)} USD`;
}
// Try to scan the DOM for products
function scanProducts(): Product[] {
  const products: Product[] = [];
  const seen = new Set<string>();
  // 1) JSON-LD structured data
  try {
    const scripts = document.querySelectorAll<HTMLScriptElement>('script[type="application/ld+json"]');
    scripts.forEach(s => {
      try {
        const data = JSON.parse(s.textContent || '');
        const items = Array.isArray(data) ? data : [data];
        items.forEach(item => {
          if (item && (item['@type'] === 'Product' || item.product)) {
            const p = item.product || item;
            const offer = Array.isArray(p.offers) ? p.offers[0] : p.offers;
            const price = offer?.price ? parseFloat(offer.price) : null;
            if (p.name && price) {
              const key = `${p.name}-${price}`;
              if (!seen.has(key)) {
                seen.add(key);
                products.push({
                  title: p.name,
                  priceNumber: price,
                  priceText: formatPrice(price),
                  thumbnailUrl: Array.isArray(p.image) ? p.image[0] : p.image
                });
              }
            }
          }
        });
      } catch {}
    });
  } catch {}
  // 2) Heuristic scan: look for the dynamic price element + hero image as primary product
  try {
    // Match elements whose text contains a price
    const allElements = Array.from(document.querySelectorAll<HTMLElement>('h1, h2, h3, span, div, p, button, a'));
    // Find headings as title candidates
    const headings = Array.from(document.querySelectorAll<HTMLElement>('h1, h2'));
    // Look for big visible prices (not strikethrough)
    const priceElements = allElements.filter(el => {
      const txt = (el.innerText || el.textContent || '').trim();
      if (!txt || txt.length > 60) return false;
      if (!PRICE_REGEX.test(txt)) return false;
      // Skip strikethrough/old prices
      const style = window.getComputedStyle(el);
      if (style.textDecorationLine.includes('line-through')) return false;
      // Skip very small text
      const fontSize = parseFloat(style.fontSize);
      if (fontSize < 14) return false;
      return true;
    });
    priceElements.forEach(priceEl => {
      const price = parsePriceNumber(priceEl.innerText || priceEl.textContent || '');
      if (!price || price < 1 || price > 999) return;
      // Find nearest section/article ancestor
      let container: HTMLElement | null = priceEl;
      for (let i = 0; i < 6 && container; i++) {
        if (container.tagName === 'SECTION' || container.tagName === 'ARTICLE' || container.tagName === 'MAIN') break;
        container = container.parentElement;
      }
      const ctx = container || document.body;
      // Find a title within that container
      const titleEl = ctx.querySelector('h1, h2') || headings.find(h => priceEl.compareDocumentPosition(h) & Node.DOCUMENT_POSITION_PRECEDING);
      const title = titleEl ? (titleEl.textContent || '').trim() : '';
      // Find a real PRODUCT image within that container — skip card logos, flags,
      // tracking pixels, and other non-product imagery.
      const imgs = Array.from(ctx.querySelectorAll<HTMLImageElement>('img'));
      const productImg = imgs.find(img => !isNonProductImage(img.src));
      const thumb = productImg?.src || DEFAULT_PRODUCT_THUMB;
      if (!title) return;
      const key = `${title.slice(0, 50)}-${price}`;
      if (seen.has(key)) return;
      seen.add(key);
      products.push({
        title: title.slice(0, 80),
        priceNumber: price,
        priceText: formatPrice(price),
        thumbnailUrl: thumb
      });
    });
  } catch {}
  return products;
}
function weightedFallback(): Product {
  const total = FALLBACK_PRODUCTS.reduce((s, f) => s + f.weight, 0);
  let r = Math.random() * total;
  for (const f of FALLBACK_PRODUCTS) {
    r -= f.weight;
    if (r <= 0) return f.product;
  }
  return FALLBACK_PRODUCTS[0].product;
}
function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}
type Notification = {
  id: number;
  name: string;
  city: string;
  product: Product;
};
export function RecentPurchases() {
  const [notification, setNotification] = useState<Notification | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const recentNamesRef = useRef<string[]>([]);
  const idRef = useRef(0);
  const reduceMotion = useReducedMotion();
  // Scan products once after mount
  useEffect(() => {
    const scanTimer = setTimeout(() => {
      const scanned = scanProducts();
      setProducts(scanned);
    }, 500); // Let the page finish rendering
    return () => clearTimeout(scanTimer);
  }, []);
  // Build a notification
  const buildNotification = (): Notification => {
    const lastNames = recentNamesRef.current;
    // Build candidate full names and avoid recent ones
    let attempts = 0;
    let fullName = '';
    do {
      const fn = pickRandom(FIRST_NAMES);
      const ln = pickRandom(LAST_NAMES);
      fullName = `${fn} ${ln.charAt(0)}.`;
      attempts++;
    } while (lastNames.includes(fullName) && attempts < 25);
    // Track last 10
    recentNamesRef.current = [...lastNames, fullName].slice(-10);
    const city = pickRandom(US_CITIES);
    const product = products.length > 0 ? pickRandom(products) : weightedFallback();
    return {
      id: ++idRef.current,
      name: fullName,
      city,
      product
    };
  };
  // Timing loop
  useEffect(() => {
    let cancelled = false;
    let showTimer: ReturnType<typeof setTimeout>;
    let hideTimer: ReturnType<typeof setTimeout>;
    const scheduleNext = (delay: number) => {
      showTimer = setTimeout(() => {
        if (cancelled) return;
        setNotification(buildNotification());
        // Hide after 4s
        hideTimer = setTimeout(() => {
          if (cancelled) return;
          setNotification(null);
          // Next one between 8-15 seconds AFTER the hide
          const nextDelay = 8000 + Math.random() * 7000;
          scheduleNext(nextDelay);
        }, 4000);
      }, delay);
    };
    // First notification at 3s
    scheduleNext(3000);
    return () => {
      cancelled = true;
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products]);
  const slideAnim = reduceMotion ? {
    initial: {
      opacity: 0
    },
    animate: {
      opacity: 1
    },
    exit: {
      opacity: 0
    },
    transition: {
      duration: 0.2
    }
  } : {
    initial: {
      opacity: 0,
      y: 80,
      scale: 0.95
    },
    animate: {
      opacity: 1,
      y: 0,
      scale: 1
    },
    exit: {
      opacity: 0,
      y: 80,
      scale: 0.95
    },
    transition: {
      type: 'spring',
      stiffness: 280,
      damping: 26
    }
  };
  return <div className="fixed bottom-20 md:bottom-6 inset-x-0 z-[60] flex justify-center pointer-events-none px-4" aria-live="polite" aria-atomic="true" data-id="element-302">
      <AnimatePresence data-id="element-303">
        {notification && <motion.div key={notification.id} {...slideAnim} className="pointer-events-auto bg-white rounded-2xl shadow-2xl border border-caramel/20 px-3 py-3 pr-4 flex items-center gap-3 max-w-md w-full" data-id="element-304">
            {/* Thumbnail */}
            <div className="shrink-0 w-12 h-12 rounded-xl overflow-hidden bg-cream-dark/30 flex items-center justify-center" data-id="element-305">
              {notification.product.thumbnailUrl ? <img src={notification.product.thumbnailUrl} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" data-id="element-306" /> : <ShoppingBag className="w-5 h-5 text-caramel" data-id="element-307" />}
            </div>

            {/* Text */}
            <div className="flex-1 min-w-0" data-id="element-308">
              <p className="text-sm text-espresso leading-snug" data-id="element-309">
                <span className="font-bold" data-id="element-310">{notification.name}</span>
                <span className="text-espresso-light" data-id="element-311"> en </span>
                <span className="font-medium" data-id="element-312">{notification.city}</span>
                <span className="text-espresso-light" data-id="element-313"> acaba de comprar </span>
                <span className="font-semibold" data-id="element-314">
                  {notification.product.editionLabel || notification.product.title}
                </span>
              </p>
              <p className="text-xs text-caramel font-bold mt-0.5 flex items-center gap-2" data-id="element-315">
                <span data-id="element-316">{notification.product.priceText}</span>
                <span className="inline-flex items-center gap-1 text-green-600 font-medium" data-id="element-317">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" data-id="element-318" />
                  Verificado
                </span>
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}